import React, { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import type { BookingSummaryProps } from './types';

const TOTAL_STEPS = 4;

const BookingSummary: React.FC<BookingSummaryProps> = ({
  bookingData,
  step,
  onBack,
}) => {
  /* ---------------- Memoized Values ---------------- */

  const formattedDate = useMemo(() => {
    if (!bookingData?.date) return null;

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .format(new Date(bookingData.date))
      .replace(/\//g, '-');
  }, [bookingData?.date]);

  /* ---------------- Render ---------------- */

  return (
    <motion.aside
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      aria-label="Booking summary"
      className="bg-gradient-to-br from-white to-gray-50 
      dark:from-[#252527] dark:to-[#1a1a1c] 
      rounded-2xl shadow-xl p-4 sm:p-5 md:p-6 
      lg:sticky lg:top-28 
      border border-gray-100 dark:border-gray-800
      w-full"
    >
      {/* ---------------- Header ---------------- */}

      <header className="flex items-center gap-2 mb-5">
        <span
          className="material-symbols-outlined text-primary text-xl sm:text-2xl"
          aria-hidden
        >
          receipt_long
        </span>

        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
          Booking Summary
        </h2>
      </header>

      {/* ---------------- Booking Details ---------------- */}

      <section className="space-y-4 mb-6 text-sm sm:text-base">
        {/* Service */}
        {bookingData?.service && (
          <DetailBlock
            icon="content_cut"
            label="Service"
            primary={bookingData.service.name}
          />
        )}

        {/* Stylist */}
        {bookingData?.stylist && (
          <DetailBlock
            icon="person"
            label="Stylist"
            primary={bookingData.stylist.name}
            secondary={bookingData.stylist.role}
          />
        )}

        {/* Date */}
        {bookingData?.date && bookingData?.time && (
          <DetailBlock
            icon="event"
            label="Date & Time"
            primary={`${formattedDate} at ${bookingData.time}`}
          />
        )}

        {/* Duration */}
        {bookingData?.service?.duration && (
          <DetailBlock
            icon="schedule"
            label="Duration"
            primary={`${bookingData.service.duration} mins`}
            noBorder
          />
        )}
      </section>

      {/* ---------------- Price ---------------- */}

      {bookingData?.service?.price && (
        <section className="bg-[#FEE2E2] dark:bg-gray-800 rounded-xl p-4 sm:p-5">
          <div className="flex justify-between items-center">
            <span className="text-[#991B1B] dark:text-gray-300 text-sm sm:text-base font-medium">
              Total Price
            </span>

            <span className="text-base sm:text-lg font-bold text-[#7F1D1D] dark:text-white">
              {bookingData.service.price}
            </span>
          </div>
        </section>
      )}

      {/* ---------------- Navigation ---------------- */}

      {step < TOTAL_STEPS && onBack && (
        <button
          onClick={onBack}
          aria-label="Go back to previous step"
          className="mt-6 w-full py-2.5 sm:py-3 px-5 border-2 border-gray-300 
          dark:border-gray-600 text-gray-700 dark:text-gray-300 
          rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 
          transition-colors font-semibold text-sm sm:text-base"
        >
          ← Back
        </button>
      )}
    </motion.aside>
  );
};

export default memo(BookingSummary);

/* ---------------- Reusable Detail Block ---------------- */

interface DetailBlockProps {
  icon: string;
  label: string;
  primary: string;
  secondary?: string;
  noBorder?: boolean;
}

const DetailBlock: React.FC<DetailBlockProps> = ({
  icon,
  label,
  primary,
  secondary,
  noBorder,
}) => {
  return (
    <div
      className={`pb-3 ${
        !noBorder && 'border-b border-gray-200 dark:border-gray-700'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="material-symbols-outlined text-primary text-lg"
          aria-hidden
        >
          {icon}
        </span>

        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      </div>

      <p className="font-semibold text-gray-900 dark:text-white ml-6 break-words">
        {primary}
      </p>

      {secondary && (
        <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
          {secondary}
        </p>
      )}
    </div>
  );
};
